import { getItemFromLocalStorage, setItemInLocalStorage } from '@utils/web-storage';
import { NavigationGuard, Route, RouteConfig } from 'vue-router';
import { app } from '@store/modules/app';
import { getAbTestGroup } from '@api/ab-test';
import { isQuickInvestLocalStorageKey } from '@constants';
import { orderForm } from '@store/modules/order-form';
import { questionnaire } from '@store/modules/questionnaire';
import { ServiceLevelName } from 'types/service-level';

const questionnaireAuthGuard: NavigationGuard<Vue> = async (to, from, next) => {
	if (app.isAuthenticated) {
		await questionnaire.getFullResponse();
		if (questionnaire.dataRetrieved && !!questionnaire.fullResponse.completionDate) {
			next({ name: 'questionnaire-results' });
		} else if (questionnaire.fullResponse) {
			next({
				path: `/questionnaire/${questionnaire.fullResponse.questions[0].urlSlug}`,
				query: { firstStep: 'true' }
			});
		}
	} else {
		next();
	}
};

export const questionnaireQuickInvestGuard: NavigationGuard<Vue> = async (to, from, next) => {
	if (!questionnaire.dataRetrieved) {
		await questionnaire.getFullResponse(true);
	}

	if (!orderForm.dataRetrieved) {
		await orderForm.getUserCheckoutDetailsData();
	}

	const starterServicename: ServiceLevelName = 'STARTER';

	const isRecommendedStarterPortfolio = questionnaire.fullResponse.recommendedServiceLevel === starterServicename;
	const isQuickInvest = getItemFromLocalStorage(isQuickInvestLocalStorageKey);
	if (isQuickInvest === null) {
		const abTestGroup = await getAbTestGroup('2021-10-26_quick-invest');
		if (abTestGroup === 'QUICK_INVEST_CHECKOUT') {
			if (isRecommendedStarterPortfolio) {
				const name = await orderForm.submitPlanSelection({
					investmentStrategy: 'STARTER_PORTFOLIO',
					planType: 'STARTER',
					serviceLevelName: starterServicename
				});
				setItemInLocalStorage(isQuickInvestLocalStorageKey, 'true');
				next({ name });
			} else {
				next({ name: 'quick-invest' });
			}
		} else {
			next();
		}
	} else if (isQuickInvest === 'true') {
		if (from.fullPath.includes('user-dashboard')) {
			if (isRecommendedStarterPortfolio) {
				setItemInLocalStorage(isQuickInvestLocalStorageKey, 'false');
				next();
			} else {
				next({ name: 'quick-invest' });
			}
		} else {
			next({ name: 'checkout-start' });
		}
	} else {
		if (from.fullPath.includes('quick-invest')) {
			next();
		} else {
			next({ name: 'quick-invest' });
		}
	}
};

export const quickInvestPageGuard: NavigationGuard<Vue> = async (to, from, next) => {
	if (!app.isAuthenticated) {
		next({ name: 'questionnaire-personal-info' });
	} else {
		if (!questionnaire.dataRetrieved) {
			await questionnaire.getFullResponse(true);
		}
		next();
	}
};

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const questionnaireRoutes: RouteConfig[] = [
	{
		path: `/questionnaire/results`,
		alias: '/questionnaire/results-cards',
		name: 'questionnaire-results',
		component: () =>
			import(/* webpackChunkName: "questionnaire-results" */ '@views/questionnaire/questionnaire-results.vue'),
		beforeEnter: questionnaireQuickInvestGuard,
		redirect: { name: 'results-select-service-level' },
		children: [
			{
				path: '',
				name: 'results-select-service-level',
				meta: {
					step: 1
				},
				component: () =>
					import(
						/* webpackChunkName: "results-select-service-level" */ '@views/questionnaire/questionnaire-results-select-service-level.vue'
					)
			},
			{
				path: 'select-core-plan',
				name: 'results-select-core-plan',
				meta: {
					step: 2
				},
				beforeEnter: async (to, from, next) => {
					if (orderForm.serviceLevel === null) {
						next({ name: 'results-select-service-level' });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "results-select-core-plan" */ '@views/questionnaire/questionnaire-results-select-core-plan.vue'
					)
			},
			{
				path: `plus-plan`,
				name: 'results-plus-plan',
				meta: {
					step: 3
				},
				beforeEnter: (to, from, next) => {
					if (orderForm.serviceLevel !== 'ADVANCED' && orderForm.serviceLevel !== 'PREMIUM') {
						next({ name: 'results-select-service-level' });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "results-plus-plan" */
						'@views/questionnaire/questionnaire-results-plus-plan.vue'
					)
			}
		]
	},
	{
		path: `/questionnaire`,
		name: 'questionnaire',
		component: () => import(/* webpackChunkName: "questionnaire" */ '@views/questionnaire/questionnaire.vue'),
		redirect: { name: 'questionnaire-personal-info' },
		beforeEnter: (to, from, next) => {
			if (to.query.new_lead) {
				next({ name: 'questionnaire-password', query: { firstStep: 'true' } });
			} else {
				next();
			}
		},
		children: [
			{
				path: `personal-info`,
				name: 'questionnaire-personal-info',
				meta: {
					step: -2
				},
				beforeEnter: questionnaireAuthGuard,
				component: () =>
					import(
						/* webpackChunkName: "questionnaire-personal-info" */ '@views/questionnaire/questionnaire-personal-info.vue'
					)
			},
			{
				path: `password`,
				name: 'questionnaire-password',
				meta: {
					step: -1
				},
				beforeEnter: questionnaireAuthGuard,
				component: () =>
					import(
						/* webpackChunkName: "questionnaire-password" */ '@views/questionnaire/questionnaire-password.vue'
					)
			},
			{
				path: `quick-invest`,
				name: 'quick-invest',
				beforeEnter: quickInvestPageGuard,
				component: () =>
					import(
						/* webpackChunkName: "questionnaire-quick-invest" */ '@views/questionnaire/questionnaire-quick-invest.vue'
					)
			},
			{
				path: `:questionUrlSlug`,
				name: 'questionnaire-question',
				meta: (route: Route) => ({
					step: questionnaire.dataRetrieved
						? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						  questionnaire.fullResponse.questions.find((q) => q.urlSlug === route.params.questionUrlSlug)!
								.sortIndex
						: -1
				}),
				beforeEnter: (to, from, next) => {
					if (!questionnaire.dataRetrieved) {
						next({ name: 'questionnaire-personal-info' });
					} else {
						next();
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "questionnaire-question" */ '@views/questionnaire/questionnaire-question.vue'
					)
			}
		]
	}
];

export default questionnaireRoutes;
